import { useQuery } from "@apollo/client";
import * as React from "react";
import { type Control } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { AccreditationsPicker } from "../AccreditationsPicker";
import { LabelsPicker } from "../LabelsPicker";
import { CONDITION_OPTIONS, OWNERSHIP_TYPE_OPTIONS } from "./constants";
import { type FieldSetting, type MetadataField } from "./types";
import { type BookingFormSchema } from "./validation";
import { sexOptions } from "@/benchmarks/BenchmarkFilters/SexPicker";
import { Combobox } from "@/components/Combobox";
import { type ComboboxOption } from "@/components/Combobox/types";
import { FormDatePicker } from "@/components/FormDatePicker";
import { FormItemWithMessage } from "@/components/FormItemWithMessage";
import { SelectInput } from "@/components/SelectInput";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { businessUnitUsersQuery } from "@/contacts/ContactForm";
import { graphql } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";
import { formatName } from "@/lib/utils/formatting";
import { sortOptionsByLabel } from "@/lib/utils/sorting";

type RenderFunctionProps = {
  control: Control<BookingFormSchema>;
  setting: FieldSetting;
};

type FieldRenderDictionary = {
  [K in MetadataField]: (props: RenderFunctionProps) => React.ReactNode;
};

export const useGetFormFields = (): FieldRenderDictionary => {
  const { activeBusinessUnitId, activeBusinessUnitIdInt } = useActiveBusinessUnit();
  const {
    data: supplyChainPartnerOptionsData,
    loading: supplyChainPartnerOptionsDataLoading,
    error: supplyChainPartnerOptionsDataError,
  } = useQuery(supplyChainPartnerOptionsQuery, {
    variables: {
      businessUnitIds: activeBusinessUnitId ? [activeBusinessUnitId] : [],
      status: ["ACTIVE"],
      sortBy: [{ field: "BUSINESS_NAME", desc: false }],
    },
  });

  const {
    data: supplyChainBookingGridsData,
    loading: supplyChainBookingGridsDataLoading,
    error: supplyChainBookingGridsDataError,
  } = useQuery(supplyChainBookingGridsQuery, {
    variables: {
      businessUnitId: activeBusinessUnitId || "",
    },
  });

  const {
    data: supplyChainBookingProgramsData,
    loading: supplyChainBookingProgramsDataLoading,
    error: supplyChainBookingProgramsDataError,
  } = useQuery(supplyChainBookingProgramsQuery, {
    variables: {
      businessUnitId: activeBusinessUnitId || "",
    },
  });

  const {
    data: usersData,
    loading: userDataLoading,
    error: userDataError,
  } = useQuery(businessUnitUsersQuery, {
    variables: { businessUnitId: activeBusinessUnitIdInt },
  });

  const vetOptions: Array<ComboboxOption> = sortOptionsByLabel(
    removeNothings(usersData?.businessUnitUsers || []).map((businessUnitUser) => {
      return {
        label: formatName({ firstName: businessUnitUser.user.firstName, lastName: businessUnitUser.user.lastName }),
        value: businessUnitUser.user.id,
      };
    }),
  );

  const programOptions: Array<ComboboxOption> = sortOptionsByLabel(
    removeNothings(supplyChainBookingProgramsData?.supplyChainBookingPrograms || [])
      .filter((program) => program.status === "ACTIVE")
      .map((program) => {
        return {
          label: program.label,
          value: program.id,
        };
      }),
  );

  const gridOptions: Array<ComboboxOption> = sortOptionsByLabel(
    removeNothings(supplyChainBookingGridsData?.supplyChainBookingGrids || [])
      .filter((grid) => grid.status === "ACTIVE")
      .map((grid) => {
        return {
          label: grid.label,
          value: grid.id,
        };
      }),
  );

  const partnerOptions: Array<ComboboxOption> = removeNothings(
    supplyChainPartnerOptionsData?.supplyChainPartners?.edges || [],
  ).map((partner) => {
    return {
      label: partner.node?.businessName || "",
      value: partner.node?.id || "",
    };
  });

  const truckingOptions = removeNothings(supplyChainPartnerOptionsData?.supplyChainPartners?.edges || [])
    .filter((partnerOption) => {
      return partnerOption.node?.role === "TRUCKING";
    })
    .map((partner) => {
      return {
        label: partner.node?.businessName || "",
        value: partner.node?.id || "",
      };
    });

  return {
    RISK_MANAGEMENT: ({ control, setting }) => {
      return (
        <FormField
          key="riskManagement"
          control={control}
          name="RISK_MANAGEMENT"
          render={({ field }) => (
            <FormItem className="flex flex-row space-x-2 space-y-0 items-center">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel>Risk Management {setting.isRequired && <span>*</span>}</FormLabel>
            </FormItem>
          )}
        />
      );
    },
    VETERINARIAN: ({ control, setting }) => {
      return (
        <FormField
          control={control}
          name="VETERINARIAN"
          render={({ field }) => (
            <FormItemWithMessage
              label="Veterinarian"
              required={setting.isRequired}
              inputComponent={
                <Combobox
                  type="single"
                  placeholder="Select veterinarian"
                  selected={field.value}
                  options={vetOptions}
                  loading={userDataLoading}
                  error={userDataError}
                  isModal
                  {...field}
                />
              }
            />
          )}
        />
      );
    },
    PROGRAM: ({ control, setting }) => {
      return (
        <FormField
          control={control}
          name="PROGRAM"
          render={({ field }) => (
            <FormItemWithMessage
              label="Program"
              required={setting.isRequired}
              inputComponent={
                <Combobox
                  type="single"
                  placeholder="Select program"
                  selected={field.value}
                  options={programOptions}
                  loading={supplyChainBookingProgramsDataLoading}
                  error={supplyChainBookingProgramsDataError}
                  isModal
                  {...field}
                />
              }
            />
          )}
        />
      );
    },
    GRID: ({ control, setting }) => {
      return (
        <FormField
          key="grid"
          control={control}
          name="GRID"
          render={({ field }) => (
            <FormItemWithMessage
              label="Grid"
              required={setting.isRequired}
              inputComponent={
                <Combobox
                  type="single"
                  placeholder="Select grid"
                  selected={field.value}
                  options={gridOptions}
                  loading={supplyChainBookingGridsDataLoading}
                  error={supplyChainBookingGridsDataError}
                  isModal
                  {...field}
                />
              }
            />
          )}
        />
      );
    },
    TRUCKING_COMPANY: ({ control, setting }) => {
      return (
        <FormField
          key="truckingCompany"
          control={control}
          name="TRUCKING_COMPANY"
          render={({ field }) => (
            <FormItemWithMessage
              label="Trucking Company"
              required={setting.isRequired}
              inputComponent={
                <Combobox
                  type="single"
                  placeholder="Select trucking company"
                  selected={field.value}
                  options={truckingOptions}
                  loading={supplyChainPartnerOptionsDataLoading}
                  error={supplyChainPartnerOptionsDataError}
                  isModal
                  {...field}
                />
              }
            />
          )}
        />
      );
    },
    BROKER: ({ control, setting }) => {
      return (
        <FormField
          key="broker"
          control={control}
          name="BROKER"
          render={({ field }) => (
            <FormItemWithMessage
              label="Broker"
              required={setting.isRequired}
              inputComponent={
                <Combobox
                  type="single"
                  placeholder="Select broker"
                  selected={field.value}
                  options={partnerOptions}
                  loading={supplyChainPartnerOptionsDataLoading}
                  error={supplyChainPartnerOptionsDataError}
                  isModal
                  {...field}
                />
              }
            />
          )}
        />
      );
    },
    ACCOUNT_NUMBER: ({ control, setting }) => {
      return (
        <FormField
          key="accountNumber"
          control={control}
          name="ACCOUNT_NUMBER"
          render={({ field }) => (
            <FormItemWithMessage
              label="Account Number"
              required={setting.isRequired}
              inputComponent={<Input placeholder="Enter account number" {...field} />}
            />
          )}
        />
      );
    },
    CERTIFICATE_NUMBER: ({ control, setting }) => {
      return (
        <FormField
          key="certificateNumber"
          control={control}
          name="CERTIFICATE_NUMBER"
          render={({ field }) => (
            <FormItemWithMessage
              label="Certificate Number"
              required={setting.isRequired}
              inputComponent={<Input placeholder="Certificate #" {...field} />}
            />
          )}
        />
      );
    },
    PURCHASE_PRICE_PER_CWT: ({ control, setting }) => {
      return (
        <FormField
          key="purchasePricePerCWT"
          control={control}
          name="PURCHASE_PRICE_PER_CWT"
          render={({ field }) => (
            <FormItemWithMessage
              label="Purchase Price per CWT"
              required={setting.isRequired}
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => {
                    field.onChange(values.value);
                  }}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  prefix={"$"}
                  maxLength={15}
                  decimalScale={2}
                  placeholder="Enter price"
                />
              }
            />
          )}
        />
      );
    },
    PRICE: ({ control, setting }) => (
      <FormField
        key="price"
        control={control}
        name="PRICE"
        render={({ field }) => (
          <FormItemWithMessage
            label="Price"
            required={setting.isRequired}
            inputComponent={
              <NumericFormat
                customInput={Input}
                getInputRef={field.ref}
                value={field.value}
                onValueChange={(values) => {
                  field.onChange(values.value);
                }}
                allowNegative={false}
                thousandSeparator=","
                decimalSeparator="."
                prefix={"$"}
                maxLength={15}
                decimalScale={2}
                placeholder="Enter price"
              />
            }
          />
        )}
      />
    ),
    FREIGHT_COST: ({ control, setting }) => (
      <FormField
        key="freight"
        control={control}
        name="FREIGHT_COST"
        render={({ field }) => (
          <FormItemWithMessage
            label="Freight"
            required={setting.isRequired}
            inputComponent={
              <NumericFormat
                customInput={Input}
                getInputRef={field.ref}
                allowNegative={false}
                value={field.value}
                onValueChange={(values) => {
                  field.onChange(values.value);
                }}
                thousandSeparator=","
                decimalSeparator="."
                prefix={"$"}
                maxLength={150}
                decimalScale={2}
                placeholder="Enter freight"
              />
            }
          />
        )}
      />
    ),
    SLIDE: ({ control, setting }) => (
      <FormField
        key="slide"
        control={control}
        name="SLIDE"
        render={({ field }) => (
          <FormItemWithMessage
            label="Slide"
            required={setting.isRequired}
            inputComponent={
              <NumericFormat
                customInput={Input}
                getInputRef={field.ref}
                allowNegative={false}
                value={field.value}
                onValueChange={(values) => {
                  field.onChange(values.value);
                }}
                thousandSeparator=","
                decimalSeparator="."
                prefix={"$"}
                maxLength={150}
                decimalScale={0}
                placeholder="Enter slide"
              />
            }
          />
        )}
      />
    ),
    SEX_CLASS: ({ control, setting }) => (
      <FormField
        key="sexClass"
        control={control}
        name="SEX_CLASS"
        render={({ field }) => (
          <FormItemWithMessage
            label="Sex"
            required={setting.isRequired}
            inputComponent={
              <SelectInput options={sexOptions} placeholder="Select sex" {...field} value={field.value || ""} />
            }
          />
        )}
      />
    ),
    PEN_NUMBER: ({ control, setting }) => (
      <FormField
        key="penNumber"
        control={control}
        name="PEN_NUMBER"
        render={({ field }) => (
          <FormItemWithMessage
            label="Pen Number"
            required={setting.isRequired}
            inputComponent={<Input placeholder="Enter pen number" {...field} />}
          />
        )}
      />
    ),
    LOT_NUMBER: ({ control, setting }) => (
      <FormField
        key="lotNumber"
        control={control}
        name="LOT_NUMBER"
        render={({ field }) => (
          <FormItemWithMessage
            label="Lot Number"
            required={setting.isRequired}
            inputComponent={<Input placeholder="Enter lot number" {...field} />}
          />
        )}
      />
    ),
    ACCREDITATIONS: ({ control, setting }) => (
      <FormField
        key="accreditations"
        control={control}
        name="ACCREDITATIONS"
        render={({ field }) => (
          <FormItemWithMessage
            label="Accreditations"
            required={setting.isRequired}
            inputComponent={
              <AccreditationsPicker
                inputRef={field.ref}
                selected={field.value || []}
                onSelect={field.onChange}
                placeholder="Select accreditations"
                isModal
              />
            }
          />
        )}
      />
    ),
    CONFIRMED: ({ control, setting }) => (
      <FormField
        key="confirmed"
        control={control}
        name="CONFIRMED"
        render={({ field }) => (
          <FormItem className="flex flex-row space-x-2 space-y-0 items-center">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <FormLabel>Confirmed {setting.isRequired && <span>*</span>}</FormLabel>
          </FormItem>
        )}
      />
    ),
    ESTIMATED_WEIGHT_NOTE: ({ control, setting }) => {
      return (
        <FormField
          key="estimatedWeightNote"
          control={control}
          name="ESTIMATED_WEIGHT_NOTE"
          render={({ field }) => (
            <FormItemWithMessage
              label="Est. Weight Note"
              className="md:col-span-3"
              required={setting.isRequired}
              inputComponent={<Textarea placeholder="Enter weight note" className="resize-none" {...field} />}
            />
          )}
        />
      );
    },
    SERVICE_PROVIDED: ({ control, setting }) => {
      return (
        <FormField
          key="serviceProvided"
          control={control}
          name="SERVICE_PROVIDED"
          render={({ field }) => (
            <FormItemWithMessage
              label="Service Provided"
              className="md:col-span-3"
              required={setting.isRequired}
              inputComponent={<Textarea placeholder="Enter note" className="resize-none" {...field} />}
            />
          )}
        />
      );
    },
    DATE_OF_SALE: ({ setting }) => {
      return (
        <FormDatePicker<BookingFormSchema>
          name={"DATE_OF_SALE"}
          key="dateOfSale"
          label="Date of Sale"
          required={setting.isRequired}
          isModal
        />
      );
    },
    DATE_OF_VISIT: ({ setting }) => {
      return (
        <FormDatePicker<BookingFormSchema>
          name={"DATE_OF_VISIT"}
          key="dateOfVisit"
          label="Date of Visit"
          required={setting.isRequired}
          isModal
        />
      );
    },
    PROJECTED_OUT_DATE: ({ setting }) => {
      return (
        <FormDatePicker<BookingFormSchema>
          name={"PROJECTED_OUT_DATE"}
          key="projectedOutDate"
          label="Projected Out Date"
          required={setting.isRequired}
          isModal
        />
      );
    },
    DATE_DELIVERED: ({ setting }) => {
      return (
        <FormDatePicker<BookingFormSchema>
          key="dateDelivered"
          name="DATE_DELIVERED"
          label="Date Delivered"
          required={setting.isRequired}
          isModal
        />
      );
    },
    SOURCE_NOTE: ({ control, setting }) => (
      <FormField
        control={control}
        name="SOURCE_NOTE"
        render={({ field }) => (
          <FormItemWithMessage
            label="Source Note"
            required={setting.isRequired}
            inputComponent={<Input placeholder="Enter note" {...field} />}
          />
        )}
      />
    ),
    BRAND_NOTE: ({ control, setting }) => (
      <FormField
        control={control}
        name="BRAND_NOTE"
        render={({ field }) => (
          <FormItemWithMessage
            label="Brand Note"
            required={setting.isRequired}
            inputComponent={<Input placeholder="Enter note" {...field} />}
          />
        )}
      />
    ),
    SHIPPED_WEIGHT: ({ control, setting }) => (
      <FormField
        control={control}
        name="SHIPPED_WEIGHT"
        render={({ field }) => {
          return (
            <FormItemWithMessage
              label="Shipped Weight"
              required={setting.isRequired}
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  allowNegative={false}
                  value={field.value}
                  onValueChange={(values) => {
                    field.onChange(values.value);
                  }}
                  thousandSeparator=","
                  decimalSeparator="."
                  maxLength={15}
                  decimalScale={0}
                  placeholder="Enter shipped weight"
                />
              }
            />
          );
        }}
      />
    ),
    BASE_WEIGHT: ({ control, setting }) => (
      <FormField
        control={control}
        name="BASE_WEIGHT"
        render={({ field }) => {
          return (
            <FormItemWithMessage
              label="Base Weight"
              required={setting.isRequired}
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  allowNegative={false}
                  value={field.value}
                  onValueChange={(values) => {
                    field.onChange(values.value);
                  }}
                  thousandSeparator=","
                  decimalSeparator="."
                  maxLength={15}
                  decimalScale={0}
                  placeholder="Enter base weight"
                />
              }
            />
          );
        }}
      />
    ),
    BREED_NOTE: ({ control, setting }) => {
      return (
        <FormField
          control={control}
          name="BREED_NOTE"
          render={({ field }) => (
            <FormItemWithMessage
              label="Breed Note"
              required={setting.isRequired}
              inputComponent={<Input placeholder="Enter note" {...field} />}
            />
          )}
        />
      );
    },
    ESTIMATED_CHOICE_PERCENT: ({ control, setting }) => (
      <FormField
        control={control}
        name="ESTIMATED_CHOICE_PERCENT"
        render={({ field }) => {
          return (
            <FormItemWithMessage
              label="Estimated Choice"
              required={setting.isRequired}
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  allowNegative={false}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => {
                    field.onChange(values.value);
                  }}
                  thousandSeparator=","
                  decimalSeparator="."
                  suffix="%"
                  maxLength={4}
                  decimalScale={0}
                  placeholder="Enter percentage"
                />
              }
            />
          );
        }}
      />
    ),
    SHRINK_PERCENT: ({ control, setting }) => (
      <FormField
        control={control}
        name="SHRINK_PERCENT"
        render={({ field }) => {
          return (
            <FormItemWithMessage
              label="Shrink Percentage"
              required={setting.isRequired}
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  allowNegative={false}
                  value={field.value}
                  onValueChange={(values) => {
                    field.onChange(values.value);
                  }}
                  thousandSeparator=","
                  decimalSeparator="."
                  suffix="%"
                  maxLength={4}
                  decimalScale={0}
                  placeholder="Enter percentage"
                />
              }
            />
          );
        }}
      />
    ),
    LABELS: ({ control, setting }) => (
      <FormField
        key="labels"
        control={control}
        name="LABELS"
        render={({ field }) => (
          <FormItemWithMessage
            label="Labels"
            required={setting.isRequired}
            inputComponent={
              <LabelsPicker
                inputRef={field.ref}
                selected={field.value || []}
                onSelect={field.onChange}
                placeholder="Select labels"
                isModal
              />
            }
          />
        )}
      />
    ),
    OWNERSHIP_TYPE: ({ control, setting }) => (
      <FormField
        control={control}
        name="OWNERSHIP_TYPE"
        render={({ field }) => {
          return (
            <FormItemWithMessage
              label="Ownership Type"
              required={setting.isRequired}
              inputComponent={
                <SelectInput
                  options={[...OWNERSHIP_TYPE_OPTIONS]}
                  placeholder="Select ownership type"
                  {...field}
                  value={field.value || ""}
                />
              }
            />
          );
        }}
      />
    ),
    CONDITION: ({ control, setting }) => (
      <FormField
        control={control}
        name="CONDITION"
        render={({ field }) => {
          return (
            <FormItemWithMessage
              label="Condition"
              required={setting.isRequired}
              inputComponent={
                <SelectInput
                  options={[...CONDITION_OPTIONS]}
                  placeholder="Select condition"
                  {...field}
                  value={field.value || ""}
                />
              }
            />
          );
        }}
      />
    ),
    CONDITIONS: ({ control, setting }) => (
      <FormField
        control={control}
        name="CONDITIONS"
        render={({ field }) => {
          return (
            <FormItemWithMessage
              label="Conditions"
              required={setting.isRequired}
              inputComponent={
                <Combobox
                  type="multi"
                  options={[...CONDITION_OPTIONS]}
                  placeholder="Select conditions"
                  selected={field.value || []}
                  onChange={field.onChange}
                />
              }
            />
          );
        }}
      />
    ),
  };
};

export const supplyChainBookingProgramsQuery = graphql(`
  query SupplyChainBookingPrograms($businessUnitId: ID!) {
    supplyChainBookingPrograms(businessUnit: $businessUnitId) {
      id
      label
      status
    }
  }
`);

export const supplyChainBookingGridsQuery = graphql(`
  query SupplyChainBookingGrids($businessUnitId: ID!) {
    supplyChainBookingGrids(businessUnit: $businessUnitId) {
      id
      label
      status
    }
  }
`);

export const supplyChainPartnerOptionsQuery = graphql(`
  query SupplyChainPartnerOptions(
    $status: [SupplyChainPartnerStatusEnumType!]
    $sortBy: [SupplyChainPartnersColumnSort]
    $businessUnitIds: [ID!]
  ) {
    supplyChainPartners(sortBy: $sortBy, owners: $businessUnitIds, status: $status) {
      edges {
        node {
          id
          businessName
          role
        }
      }
    }
  }
`);
